/**
 * `useUpNext(limit)` — the titles in progress for Tonight's Up next
 * (`limit` 10: the hero + the "Also in progress" rail), the Library rail
 * (uncapped) and the palette: `useWholeLibrary` (every `/v1/home` page,
 * most recently updated first — not just the loaded shelf pages, or older
 * in-progress series would silently drop out) joined with `useMyCalendar`
 * for the NEW flag.
 *
 * Progressive (ui-ux.md §7): it renders from the first page and keeps filling
 * in while later pages load; `/v1/home` is most-recent-first, so Tonight's
 * ten usually come from page one and never reorder. `isPending` = the first
 * page has not landed — or it landed with nothing in progress while more
 * pages are still coming, so the empty state never flashes ahead of a later
 * page's series. The calendar is an enhancement: while it loads or if it
 * fails, Up next still renders (no NEW tags).
 */

import { useMemo } from 'react';

import { useMyCalendar } from '@/features/calendar/api';
import { useWholeLibrary } from '@/features/library/api';

import { buildUpNext } from './model';

export function useUpNext(limit = 4) {
  const library = useWholeLibrary();
  const calendar = useMyCalendar();
  const libraryItems = library.data?.items;
  const items = useMemo(
    () => buildUpNext(libraryItems ?? [], calendar.data, Date.now(), limit),
    [libraryItems, calendar.data, limit],
  );
  const loadingMore = library.hasNextPage && !library.isError;
  return {
    items,
    isPending: library.isPending || (items.length === 0 && loadingMore),
    isError: library.isError && !library.data,
    refetch: () => void library.refetch(),
  };
}
